import { z } from 'zod';

/**
 * Response envelope + closed error-code union for the JSON API
 * (ARCHITECTURE_PLAN §9). Defined in `src/shared` so the Worker (producer) and
 * the SPA (consumer) validate against a single source of truth (§3).
 *
 * Every API response is either `{ ok: true, data }` or
 * `{ ok: false, error: { code, message, requestId } }`. The error code union is
 * CLOSED — new failure modes must map onto one of these codes (see
 * `src/tenancy/errors.ts` for the typed error classes that carry them).
 */

export const ERROR_CODES = [
  'unauthorized',
  'forbidden',
  'not_found',
  'validation_failed',
  'conflict',
  'usage_limit_exceeded',
  'provider_error',
  'rate_limited',
  'internal',
] as const;

/** Zod enum over the closed §9 error-code union. */
export const ErrorCodeSchema = z.enum(ERROR_CODES);

export type ErrorCode = z.infer<typeof ErrorCodeSchema>;

/** Client-visible error body. `message` is always a SAFE, actionable message. */
export const ErrorBodySchema = z.object({
  code: ErrorCodeSchema,
  message: z.string(),
  requestId: z.string(),
});

/** Failure envelope. */
export const ErrEnvelopeSchema = z.object({
  ok: z.literal(false),
  error: ErrorBodySchema,
});

export type ErrEnvelope = z.infer<typeof ErrEnvelopeSchema>;
export type ErrorBody = z.infer<typeof ErrorBodySchema>;

/** Success envelope (data payload type varies per endpoint). */
export interface OkEnvelope<T> {
  ok: true;
  data: T;
}

export type Envelope<T> = OkEnvelope<T> | ErrEnvelope;

/**
 * Build a zod schema for a success envelope whose `data` is validated by the
 * given schema. Used by the SPA client and contract tests per endpoint.
 */
export function okEnvelopeSchema<T extends z.ZodTypeAny>(data: T) {
  return z.object({ ok: z.literal(true), data });
}

/** Success envelope constructor. */
export function ok<T>(data: T): OkEnvelope<T> {
  return { ok: true, data };
}

/** Failure envelope constructor. `message` must already be client-safe. */
export function err(code: ErrorCode, message: string, requestId: string): ErrEnvelope {
  return { ok: false, error: { code, message, requestId } };
}
